import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import { DeviceSize } from "constants/deviceSize";
import { Track, moveTrackToTrash, loadDb, downloadTrackAsMp3, downloadTrackAsWav } from "../data/schema";
import { addToQueue, getQueue } from "../data/queue";
import { showToast } from "components/ui-kit/Toast";
import { BASE_URL_SHARE } from "../../../constants/api";
import TmDelIcon from "assets/tm-del.svg";
import TmDownIcon from "assets/tm-down.svg";
import TmMoveIcon from "assets/tm-move.svg";
import TmMp3Icon from "assets/tm-mp3.svg";
import TmWavIcon from "assets/tm-wav.svg";
import TmPlayIcon from "assets/tm-play.svg";
import TmQueIcon from "assets/tm-que.svg";
import TmRepIcon from "assets/tm-rep.svg";
import TmShareIcon from "assets/tm-share.svg";
import TmLinkIcon from "assets/tm-link.svg";
import TmShtoIcon from "assets/tm-shto.svg";
import TmBugIcon from "assets/tm-bug.svg";
import TmInnIcon from "assets/tm-inn.svg";

type TrackMenuProps = {
  trackId: string;
  track: Track;
  onClose: () => void;
  onAddToPlaylist?: () => void;
  onMoveToWorkspace?: () => void;
  onCreatePersona?: () => void;
  onAddToQueue?: () => void;
  onMoveToTrash?: () => void;
  onReportBug?: () => void;
  onReportInappropriate?: () => void;
  onShareTo?: () => void;
  selectedTracks?: Set<string>; // Set of selected track IDs for bulk operations
  submenuOpen: 'share' | 'download' | 'report' | null;
  onSubmenuOpen: (type: 'share' | 'download' | 'report' | null) => void;
};

export const TrackMenu: React.FC<TrackMenuProps> = ({ trackId, track, onClose, onAddToPlaylist, onMoveToWorkspace, onCreatePersona, onAddToQueue, onMoveToTrash, onReportBug, onReportInappropriate, onShareTo, selectedTracks, submenuOpen, onSubmenuOpen }) => {
  // Determine if we have multiple tracks selected (2+)
  const hasMultipleSelection = selectedTracks && selectedTracks.size >= 2;
  
  // Get all selected tracks for bulk operations
  const getSelectedTracks = (): Track[] => {
    if (!selectedTracks || selectedTracks.size === 0) {
      return [track];
    }
    const db = loadDb();
    return Array.from(selectedTracks)
      .map(id => db.tracks[id])
      .filter(Boolean) as Track[];
  };
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmenuOpen = (type: 'share' | 'download' | 'report' | null) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    onSubmenuOpen(type);
  };

  const handleSubmenuClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      onSubmenuOpen(null);
    }, 200); // 200ms delay before closing
  };

  const handleSubmenuEnter = (type: 'share' | 'download' | 'report') => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    onSubmenuOpen(type);
  };

  const handleMenuContainerLeave = () => {
    // Only close if no submenu is open
    if (!submenuOpen) {
      onSubmenuOpen(null);
    }
  };
  return (
    <MenuContainer onClick={(e) => e.stopPropagation()} data-track-menu onMouseLeave={handleMenuContainerLeave}>
      <MenuSection>
        <MenuItem onClick={(e) => { 
          e.stopPropagation(); 
          const queue = getQueue();
          if (!queue) {
            // No queue exists, show error
            showToast("No active playlist. Please start playing a track first.", undefined, 'error');
            onClose();
            return;
          }
          
          const tracksToAdd = getSelectedTracks();
          let addedCount = 0;
          let alreadyInQueueCount = 0;
          
          tracksToAdd.forEach(t => {
            const success = addToQueue(t);
            if (success) {
              addedCount++;
            } else {
              alreadyInQueueCount++;
            }
          });
          
          if (addedCount > 0) {
            showToast(
              addedCount === 1 
                ? "Track added to queue" 
                : `${addedCount} tracks added to queue`,
              undefined,
              'success'
            );
          }
          if (alreadyInQueueCount > 0) {
            showToast(
              `${alreadyInQueueCount} track(s) already in queue`,
              undefined,
              'error'
            );
          }
          
          onAddToQueue?.();
          onClose(); 
        }}>
          <MenuItemIcon>
            <TmQueIcon width="20" height="20" />
          </MenuItemIcon>
          <MenuItemText>Add to Queue</MenuItemText>
        </MenuItem>
        <MenuItem onClick={(e) => { 
          e.stopPropagation(); 
          onAddToPlaylist?.(); 
          onClose(); 
        }}>
          <MenuItemIcon>
            <TmPlayIcon width="20" height="20" />
          </MenuItemIcon>
          <MenuItemText>Add to Playlist</MenuItemText>
        </MenuItem>
        <MenuItem onClick={(e) => { 
          e.stopPropagation(); 
          onMoveToWorkspace?.(); 
          onClose(); 
        }}>
          <MenuItemIcon>
            <TmMoveIcon width="20" height="20" />
          </MenuItemIcon>
          <MenuItemText>Move to Workspace</MenuItemText>
        </MenuItem>
        <MenuItem
          $disabled={hasMultipleSelection || Boolean((track as any).params?.localPersonId)}
          onClick={(e) => {
            e.stopPropagation();
            const alreadyBound = Boolean((track as any).params?.localPersonId);
            if (hasMultipleSelection || alreadyBound) return;
            onCreatePersona?.();
            onClose();
          }}
        >
          <MenuItemIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a5 5 0 0 1 5 5v1a5 5 0 0 1-10 0V7a5 5 0 0 1 5-5z"/>
              <path d="M4 22a8 8 0 0 1 16 0"/>
            </svg>
          </MenuItemIcon>
          <MenuItemText>Create Persona</MenuItemText>
        </MenuItem>
      </MenuSection>
      <MenuDivider />
      <MenuSection>
        <MenuItemWithSubmenu
          $disabled={hasMultipleSelection}
          onMouseEnter={() => !hasMultipleSelection && handleSubmenuOpen('share')}
          onMouseLeave={handleSubmenuClose}
        >
          <MenuItemIcon>
            <TmShareIcon width="20" height="20" />
          </MenuItemIcon>
          <MenuItemText>Share</MenuItemText>
          <MenuItemArrow>›</MenuItemArrow>
          {submenuOpen === 'share' && (
            <Submenu onMouseEnter={() => handleSubmenuEnter('share')} onMouseLeave={handleSubmenuClose}>
              <SubmenuItem onClick={async (e) => { 
                e.stopPropagation(); 
                // Use UUID in share link when available
                const uuid =
                  (track as any).params?.uuid ??
                  (track as any).params?.trackUuid ??
                  null;
                const effectiveSlug = uuid || (track as any).params?.sunoTrackId || track.id;
                const trackUrl = `${BASE_URL_SHARE}/song/${effectiveSlug}`;
                try {
                  await navigator.clipboard.writeText(trackUrl);
                  showToast("Link copied to clipboard!", undefined, 'success');
                } catch (error) {
                  showToast("Failed to copy link", undefined, 'error');
                }
                onClose(); 
              }}>
                <SubmenuIcon>
                  <TmLinkIcon width="20" height="20" />
                </SubmenuIcon>
                <SubmenuText>Copy Link</SubmenuText>
              </SubmenuItem>
              <SubmenuItem onClick={(e) => { 
                e.stopPropagation(); 
                onShareTo?.();
                onClose(); 
              }}>
                <SubmenuIcon>
                  <TmShtoIcon width="20" height="20" />
                </SubmenuIcon>
                <SubmenuText>Share to...</SubmenuText>
              </SubmenuItem>
            </Submenu>
          )}
        </MenuItemWithSubmenu>
        <MenuItemWithSubmenu
          $disabled={hasMultipleSelection}
          onMouseEnter={() => !hasMultipleSelection && handleSubmenuOpen('download')}
          onMouseLeave={handleSubmenuClose}
        >
          <MenuItemIcon>
            <TmDownIcon width="20" height="20" />
          </MenuItemIcon>
          <MenuItemText>Download</MenuItemText>
          <MenuItemArrow>›</MenuItemArrow>
          {submenuOpen === 'download' && (
            <Submenu onMouseEnter={() => handleSubmenuEnter('download')} onMouseLeave={handleSubmenuClose}>
              <SubmenuItem onClick={async (e) => { 
                e.stopPropagation(); 
                try {
                  await downloadTrackAsMp3(track);
                  showToast("Download started", undefined, "success");
                } catch (error: any) {
                  const message = error?.message || "Failed to download MP3";
                  showToast(message, undefined, "error");
                }
                onClose(); 
              }}>
                <SubmenuIcon>
                  <TmMp3Icon width="20" height="20" />
                </SubmenuIcon>
                <SubmenuText>MP3 Audio</SubmenuText>
              </SubmenuItem>
              <SubmenuItem onClick={async (e) => { 
                e.stopPropagation(); 
                // Close menu immediately
                onClose();
                // Show toast with track name
                const trackTitle = track.title || "Track";
                showToast(`${trackTitle} is downloading in WAV format`, undefined, "success");
                // Start download in background
                try {
                  await downloadTrackAsWav(track);
                } catch (error: any) {
                  const message = error?.message || "Failed to download WAV";
                  showToast(message, undefined, "error");
                }
              }}>
                <SubmenuIcon>
                  <TmWavIcon width="20" height="20" />
                </SubmenuIcon>
                <SubmenuText>WAV Audio</SubmenuText>
               
              </SubmenuItem>
            </Submenu>
          )}
        </MenuItemWithSubmenu>
      </MenuSection>
      <MenuDivider />
      <MenuSection>
        <MenuItemWithSubmenu
          $disabled={hasMultipleSelection}
          onMouseEnter={() => !hasMultipleSelection && handleSubmenuOpen('report')}
          onMouseLeave={handleSubmenuClose}
        >
          <MenuItemIcon>
            <TmRepIcon width="20" height="20" />
          </MenuItemIcon>
          <MenuItemText>Report</MenuItemText>
          <MenuItemArrow>›</MenuItemArrow>
          {submenuOpen === 'report' && (
            <Submenu onMouseEnter={() => handleSubmenuEnter('report')} onMouseLeave={handleSubmenuClose}>
              <SubmenuItem onClick={(e) => { 
                e.stopPropagation(); 
                onReportBug?.();
                onClose(); 
              }}>
                <SubmenuIcon>
                  <TmBugIcon width="20" height="20" />
                </SubmenuIcon>
                <SubmenuText>Bug</SubmenuText>
              </SubmenuItem>
              <SubmenuItem onClick={(e) => { 
                e.stopPropagation(); 
                onReportInappropriate?.();
                onClose(); 
              }}>
                <SubmenuIcon>
                  <TmInnIcon width="20" height="20" />
                </SubmenuIcon>
                <SubmenuText>Inappropriate</SubmenuText>
              </SubmenuItem>
            </Submenu>
          )}
        </MenuItemWithSubmenu>
        <MenuItem onClick={async (e) => { 
          e.stopPropagation(); 
          const tracksToDelete = getSelectedTracks();
          onClose();
          
          let successCount = 0;
          let errorCount = 0;
          
          // Process tracks sequentially
          for (const t of tracksToDelete) {
            try {
              await moveTrackToTrash(t.id);
              successCount++;
            } catch (error) {
              errorCount++;
              console.error(`Failed to delete track ${t.id}:`, error);
            }
          }
          
          if (successCount > 0) {
            showToast(
              successCount === 1
                ? "Track moved to trash"
                : `${successCount} tracks moved to trash`,
              undefined,
              'success'
            );
          }
          if (errorCount > 0) {
            showToast(
              `${errorCount} track(s) failed to delete`,
              undefined,
              'error'
            );
          }
          
          onMoveToTrash?.();
        }}>
          <MenuItemIcon>
            <TmDelIcon width="20" height="20" />
          </MenuItemIcon>
          <MenuItemText>Move to Trash</MenuItemText>
        </MenuItem>
      </MenuSection>
    </MenuContainer>
  );
};

const MenuContainer = styled.div`
  position: absolute;
  right: 0;
  top: 36px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.12);
  min-width: 240px;
  z-index: 20;
  padding: 8px 0;

  /* When used inside MusicPlayer (wrapper with data-player-menu), open menu upwards */
  [data-player-menu='true'] & {
    top: auto;
    bottom: 36px;
  }
`;

const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  z-index:20;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #e9ecef;
  margin: 4px 0;
`;

const MenuItem = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  text-align: left;
  color: ${p => p.$disabled ? '#9aa0a6' : '#333'};
  opacity: ${p => p.$disabled ? 0.6 : 1};
  transition: background 0.15s ease;
    z-index:20;

  &:hover {
    background: ${p => p.$disabled ? 'transparent' : '#f8f9ff'};
  }
`;

const MenuItemWithSubmenu = styled.div<{ $disabled?: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  color: ${p => p.$disabled ? '#999' : '#333'};
  transition: background 0.15s ease;
  opacity: ${p => p.$disabled ? 0.5 : 1};
    z-index:20;

  &:hover {
    background: ${p => p.$disabled ? 'transparent' : '#f8f9ff'};
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    position: static;
  }
`;

const MenuItemIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: inherit;
  svg {
    width: 100%;
    height: 100%;
  }
`;

const MenuItemText = styled.span`
  flex: 1;
  font-size: 14px;
  color: inherit;
`;

const MenuItemArrow = styled.span`
  color: #9aa0a6;
  font-size: 18px;
  margin-left: auto;
`;

const Submenu = styled.div`
  position: absolute;
  right: 100%;
  top: 0;
  margin-right: 2px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.12);
  min-width: 220px;
  padding: 8px 0;
  z-index: 30;

  /* If TrackModal is open (body.track-modal-open) and this is NOT a library modal,
     show submenu on the right side instead of left */
  body.track-modal-open:not(.track-modal-open-library) & {
    right: auto;
    left: 100%;
    margin-right: 0;
    margin-left: 2px;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    margin: 8px 0 0 0;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
    border-radius: 12px;
  }
`;

const SubmenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: #333;
  transition: background 0.15s ease;
  z-index:20;
  &:hover {
    background: #f8f9ff;
  }
`;

const SubmenuIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #333;
  svg {
    width: 100%;
    height: 100%;
  }
`;

const SubmenuText = styled.span`
  flex: 1;
  font-size: 14px;
  color: #333;
`;

const ProBadge = styled.span`
  background: #6777FF;
  color: #fff;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
  margin-left: auto;
`;

